import { Injectable } from "@angular/core";
import { HotToastService } from "@ngxpert/hot-toast";
import {
  CustomToastData,
  CustomToastType,
  CustomToastV1,
} from "../shared/components/custom-toast-v1/custom-toast-v1";
import { readApiErrorMessage } from "../utils/api-error.util";

type ToastOptions = {
  message?: string;
  duration?: number;
};

@Injectable({
  providedIn: "root",
})
export class ToastService {
  private notificationId = 0;

  constructor(private readonly hotToastService: HotToastService) {}

  success(title: string, options?: ToastOptions | number): void {
    const opts = typeof options === "number" ? { duration: options } : options;
    this.show("success", title, opts);
  }

  error(error: unknown, fallback = "Something went wrong. Please try again.", timer?: number): void {
    const message = readApiErrorMessage(error, fallback);
    if (!message) {
      return;
    }
    this.errorMessage(message, timer);
  }

  errorMessage(message: string, timer?: number): void {
    if (!message.trim()) {
      return;
    }
    this.show("error", message, timer !== undefined ? { duration: timer } : undefined);
  }

  info(title: string, options?: ToastOptions): void {
    this.show("info", title, options);
  }

  warning(title: string, options?: ToastOptions): void {
    this.show("warning", title, options);
  }

  private show(type: CustomToastType, title: string, options?: ToastOptions): void {
    const toastData: CustomToastData = {
      type,
      title,
      message: options?.message,
      duration: options?.duration ?? this.calculateDuration(title, options?.message, type),
      isExpandable: true,
      showConfirmation: true,
    };

    this.notificationId += 1;
    this.hotToastService.show<CustomToastData>(CustomToastV1, {
      duration: toastData.duration,
      autoClose: true,
      position: "top-right",
      id: `custom-toast-v1-${this.notificationId}`,
      style: {
        "--hot-toast-padding": "0",
        "--hot-toast-message-margin": "0",
        "--hot-toast-border-radius": "16px",
        "--hot-toast-shadow": "none",
        "--hot-toast-max-width": "350px",
        width: "350px",
      },
      data: toastData,
    });
  }

  private calculateDuration(
    title: string,
    message?: string,
    type: CustomToastType = "info",
  ): number {
    if (type === "success") {
      return 3000;
    }

    const content = `${title} ${message ?? ""}`;
    const baseTime = 2000;
    const charTime = 50;
    const calculated = baseTime + content.length * charTime;

    return Math.min(Math.max(calculated, 5000), 8000);
  }
}
